import {
  PartnerProviderLog,
  PartnerProviderLogEventType,
  PartnerProviderLogLevel,
  PartnerProviderLogStatus,
} from "@/types/partner-provider-log";

export type PartnerProviderState =
  | "healthy"
  | "error"
  | "apiError"
  | "syncing"
  | "triggered"
  | "unknown";

export const getLatestPartnerProviderLog = (
  logs: PartnerProviderLog[],
): PartnerProviderLog | null => {
  if (logs.length === 0) return null;

  return logs.reduce((latestLog, currentLog) => {
    return new Date(currentLog.createdAt) > new Date(latestLog.createdAt)
      ? currentLog
      : latestLog;
  });
};

export const getLatestFailedPartnerProviderLog = (
  logs: PartnerProviderLog[],
): PartnerProviderLog | null => {
  const failedLogs = logs.filter(
    (log) =>
      log.eventType === PartnerProviderLogEventType.SYNC_FAILED ||
      log.eventType === PartnerProviderLogEventType.API_FAILED ||
      log.status === PartnerProviderLogStatus.FAILED,
  );

  return getLatestPartnerProviderLog(failedLogs);
};

export const getPartnerProviderState = (
  log: PartnerProviderLog | null,
): PartnerProviderState => {
  if (!log) return "unknown";

  if (log.eventType === PartnerProviderLogEventType.API_FAILED) {
    return "apiError";
  }

  if (
    log.eventType === PartnerProviderLogEventType.SYNC_FAILED ||
    log.status === PartnerProviderLogStatus.FAILED
  ) {
    return "error";
  }

  if (log.eventType === PartnerProviderLogEventType.SYNC_STARTED) {
    return "syncing";
  }

  if (
    log.eventType === PartnerProviderLogEventType.SYNC_COMPLETED &&
    log.status === PartnerProviderLogStatus.SUCCESS
  ) {
    return "healthy";
  }

  if (log.eventType === PartnerProviderLogEventType.MANUAL_SYNC_TRIGGERED) {
    return "triggered";
  }

  return "unknown";
};

// A run that applied only part of its changes: SYNC_COMPLETED with status
// SUCCESS and level WARN, or SYNC_FAILED whose counters at the failure show
// that changes were already applied (details.created/updated/deactivated > 0).
export const isPartialPartnerProviderLog = (
  log: PartnerProviderLog,
): boolean => {
  if (
    log.eventType === PartnerProviderLogEventType.SYNC_COMPLETED &&
    log.status === PartnerProviderLogStatus.SUCCESS &&
    log.level === PartnerProviderLogLevel.WARN
  ) {
    return true;
  }

  if (log.eventType !== PartnerProviderLogEventType.SYNC_FAILED) return false;

  const details = log.details as Record<string, unknown> | null;
  if (!details || typeof details !== "object") return false;

  return ["created", "updated", "deactivated"].some((key) => {
    const value = details[key];
    return typeof value === "number" && value > 0;
  });
};

// Key into partners.admin.logStatusValues: PARTIAL replaces SUCCESS and FAILED for a partial run.
export const getPartnerProviderLogStatusKey = (
  log: PartnerProviderLog,
): string => (isPartialPartnerProviderLog(log) ? "PARTIAL" : log.status);

export const formatDurationMs = (durationMs: number | null): string => {
  if (durationMs === null || Number.isNaN(durationMs)) return "-";

  if (durationMs < 1000) return `${durationMs} ms`;

  return `${(durationMs / 1000).toFixed(2)} s`;
};

export const stringifyLogDetails = (details: unknown): string => {
  if (details === null || details === undefined) return "-";

  if (typeof details === "string") return details;

  try {
    return JSON.stringify(details, null, 2);
  } catch {
    return String(details);
  }
};
